import { ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

export const Button = ({
  type = "button",
  variant = "primary",
  title = "",
  height = "medium",
  leftIcon = null,
  rightIcon = null,
  onlyIcon = null,
  className = "",
  disabled,
  onClick,
  isLink = false,
  href = "/",
  target,
  dataTestid,
}: {
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "white" | "red" | "outline" | "text";
  title?: string;
  height?: "xxsmall" | "xsmall" | "small" | "medium" | "large";
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onlyIcon?: ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  isLink?: boolean;
  href?: string;
  target?: string;
  dataTestid?: string;
}) => {
  let variantStyles;
  switch (variant) {
    case "secondary":
      variantStyles = "bg-primary-108 text-primary-100";
      break;
    case "white":
      variantStyles = "bg-white-100 text-middleGray";
      break;
    case "red":
      variantStyles = "bg-red-8 text-red-100";
      break;
    case "outline":
      variantStyles =
        "border border-primary-100 bg-transparent text-primary-100";
      break;
    case "text":
      variantStyles = "bg-transparent text-primary-200 px-0";
      break;
    case "primary":
    default:
      variantStyles = "bg-primary-100 text-white-100";
  }

  let heightStyles, onlyIconStyles;
  switch (height) {
    case "xxsmall":
      heightStyles = "h-8";
      onlyIconStyles = "px-2";
      break;
    case "xsmall":
      heightStyles = "h-10";
      onlyIconStyles = "px-2";
      break;
    case "small":
      heightStyles = "h-12";
      onlyIconStyles = "px-3";
      break;
    case "medium":
      heightStyles = "h-14";
      onlyIconStyles = "px-5";
      break;
    case "large":
    default:
      heightStyles = "h-16";
      onlyIconStyles = "px-6";
  }

  let disabledStyles = "text-black-32 hover:opacity-100 ";
  switch (variant) {
    case "outline":
      disabledStyles += "border-black-8";
      break;
    case "text":
      disabledStyles += "bg-transparent";
      break;
    case "primary":
    case "secondary":
    case "white":
    default:
      disabledStyles += "bg-black-8";
  }

  return !isLink ? (
    <button
      type={type}
      className={clsx(
        "text-button",
        twMerge(
          clsx(
            "flex items-center justify-center gap-x-2 rounded-lg px-6 transition-opacity duration-300 hover:opacity-80",
            heightStyles,
            variantStyles,
            !!onlyIcon && onlyIconStyles,
            disabled && disabledStyles,
            className,
          ),
        ),
      )}
      disabled={disabled}
      onClick={onClick}
      data-testid={dataTestid}
    >
      {onlyIcon || (
        <>
          {leftIcon && leftIcon}
          {title}
          {rightIcon && rightIcon}
        </>
      )}
    </button>
  ) : (
    <Link
      to={href}
      target={target}
      className={clsx(
        "text-button",
        twMerge(
          clsx(
            "flex items-center justify-center gap-x-2 rounded-lg px-6 transition-opacity duration-300 hover:opacity-80",
            heightStyles,
            variantStyles,
            !!onlyIcon && onlyIconStyles,
            disabled && "pointer-events-none bg-lightGray text-white-100",
            className,
          ),
        ),
      )}
      data-testid={dataTestid}
    >
      {onlyIcon || (
        <>
          {leftIcon && leftIcon}
          {title}
          {rightIcon && rightIcon}
        </>
      )}
    </Link>
  );
};
